export const VIEWPORT_WINDOW_LEVEL_DRAG_MARKER = 'KAIROS_VIEWPORT_WINDOW_LEVEL_RAF_DIRECT_DOM';

const copyWindowLevel = value => value ? { ...value } : null;

export function createWindowLevelDragCoordinator({
  requestFrame,
  cancelFrame,
  onFrame,
  consumeEvidenceInput = null,
}) {
  if (typeof requestFrame !== 'function' || typeof cancelFrame !== 'function' || typeof onFrame !== 'function') {
    throw new TypeError('window level drag coordinator requires frame scheduling callbacks');
  }
  if (consumeEvidenceInput !== null && typeof consumeEvidenceInput !== 'function') {
    throw new TypeError('window level drag coordinator evidence consumer is invalid');
  }

  let frameId = null;
  let latest = null;
  let latestEvidenceInput = null;
  let latestVersion = 0;
  let dispatchedVersion = 0;
  let inFlight = false;
  let flushPending = false;
  let epoch = 0;

  const cancelScheduledFrame = () => {
    if (frameId != null) cancelFrame(frameId);
    frameId = null;
  };

  let queueLatest;
  const dispatchLatest = scheduledEpoch => {
    if (scheduledEpoch !== epoch || inFlight || !latest || latestVersion <= dispatchedVersion) return false;
    const frameValue = copyWindowLevel(latest);
    const frameEvidenceInput = latestEvidenceInput;
    const frameVersion = latestVersion;
    dispatchedVersion = frameVersion;
    inFlight = true;
    let completed = false;
    const complete = () => {
      if (completed) return;
      completed = true;
      if (scheduledEpoch !== epoch) return;
      inFlight = false;
      if (flushPending && latest && latestVersion > dispatchedVersion) {
        flushPending = false;
        dispatchLatest(epoch);
      } else {
        flushPending = false;
        queueLatest();
      }
    };
    try {
      let evidenceApplied = false;
      if (frameEvidenceInput && consumeEvidenceInput) {
        try {
          evidenceApplied = consumeEvidenceInput(frameEvidenceInput, frameValue) === true;
        } catch (_) {
          evidenceApplied = false;
        }
      }
      if (evidenceApplied) onFrame(frameValue, complete, frameEvidenceInput);
      else onFrame(frameValue, complete);
    } catch (error) {
      complete();
      throw error;
    }
    return true;
  };

  queueLatest = () => {
    if (frameId != null || inFlight || !latest || latestVersion <= dispatchedVersion) return;
    const scheduledEpoch = epoch;
    frameId = requestFrame(() => {
      frameId = null;
      dispatchLatest(scheduledEpoch);
    });
  };

  return {
    schedule(value, evidenceInput = null) {
      latest = copyWindowLevel(value);
      latestEvidenceInput = evidenceInput;
      latestVersion += 1;
      queueLatest();
    },
    flushLatest() {
      if (!latest || latestVersion <= dispatchedVersion) return false;
      cancelScheduledFrame();
      if (inFlight) {
        flushPending = true;
        return true;
      }
      return dispatchLatest(epoch);
    },
    takeLatest() {
      const snapshot = copyWindowLevel(latest);
      epoch += 1;
      cancelScheduledFrame();
      inFlight = false;
      flushPending = false;
      latest = null;
      latestEvidenceInput = null;
      dispatchedVersion = latestVersion;
      return snapshot;
    },
    cancel() {
      epoch += 1;
      cancelScheduledFrame();
      inFlight = false;
      flushPending = false;
      latest = null;
      latestEvidenceInput = null;
      dispatchedVersion = latestVersion;
    },
    snapshot() {
      return { scheduled: frameId != null, inFlight, latest: copyWindowLevel(latest) };
    },
  };
}
